import { Grid, Link, Tooltip, Avatar, Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { IUserDTO, IWorkspace, getWorkspaceBUUsers } from 'wisestep-api';
import {
  WsDataTable,
  WsListingLoader,
  WsTypography,
  WsCard,
  CardVariant,
  Item,
  EmptyTeamsAndUsersIcon,
  WsTypoVariants,
} from 'wisestep-base';

import { getIdByEmail } from '../../candidates/utils/CandidateHelper';
import { stringAvatar } from '../../recruiterProfileDetails/components/RecruiterAvatarGroup';
import { StatusTypography } from '../../users/components/TotalInvitesList';
import { getUserStatus } from '../../users/helper/defaultValues';

const avatarStyling = (value) => {
  const avatarSize = 28;
  const props = { ...stringAvatar(value) };
  props.sx['width'] = avatarSize;
  props.sx['height'] = avatarSize;
  props.sx['fontSize'] = avatarSize / 2;
  props.sx['cursor'] = 'default';
  return props;
};

interface IUsersDataStatus {
  workspaceUsers: IWorkspace[];
  totalUsers: number;
}
interface IBusinessUnitUsersTab {
  BusinessUnitId?: string;
}

const BusinessUnitUsersTab = ({ BusinessUnitId }: IBusinessUnitUsersTab) => {
  const recruiters = useRef<IUserDTO[]>();
  const [isLoading, setLoading] = useState<boolean>(true);

  const [workspaceData, setWorkspaceData] = useState<IUsersDataStatus>({
    workspaceUsers: [],
    totalUsers: 0,
  });

  useEffect(() => {
    const getInvitedUsers = async () => {
      try {
        setLoading(true);
        const response = await getWorkspaceBUUsers(BusinessUnitId ?? '');
        const workspace = response.data;

        setWorkspaceData({
          workspaceUsers: workspace.workspaceUsers,
          totalUsers: workspace.workspaceUsers?.length,
        });
        setLoading(false);
      } catch (e) {
        setWorkspaceData({
          workspaceUsers: [],
          totalUsers: 0,
        });
        setLoading(false);
      }
    };
    getInvitedUsers();
  }, [BusinessUnitId]);

  const getSelectedManagers = workspaceData.workspaceUsers?.map((item) => ({
    name: !isEmpty(item.userName?.trim()) ? item.userName : 'Not Available',
    email: item.userId,
    role: item.role,
    status: getUserStatus(item),
  }));
  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: (cell) => (
        <Grid container alignContent={'center'}>
          <Grid item xs={3}>
            <Avatar {...avatarStyling(cell.value)}>
              {cell.value
                ?.split(' ')
                .filter((e, i) => i < 2)
                .map((w) => w?.[0]?.toUpperCase() ?? '')
                .join('')}
            </Avatar>
          </Grid>
          <Grid item xs={9} mt={0.5}>
            <WsTypography bold={true}>{cell.value}</WsTypography>
          </Grid>
        </Grid>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: (cell) => (
        <Tooltip title={`View Profile`}>
          <Link
            sx={{ cursor: 'pointer' }}
            underline="none"
            onClick={() =>
              getIdByEmail(
                cell.value?.toLowerCase(),
                recruiters.current?.find((user) => user.email === cell.value?.toLowerCase())
              )
            }
          >
            {cell.value}
          </Link>
        </Tooltip>
      ),
    },
    {
      Header: 'Role',
      accessor: 'role',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: (cell) => <StatusTypography cell={cell?.value}>{cell?.value}</StatusTypography>,
    },
  ];

  return (
    <Grid container mt={2} justifyContent={'space-between'}>
      <Grid container item xs={12}>
        <Grid item xs={12}>
          {isLoading ? (
            <WsListingLoader />
          ) : (
            <>
              {isEmpty(workspaceData?.workspaceUsers) ? (
                <>
                  <WsCard
                    title={''}
                    sx={{ mt: 4 }}
                    isButton={false}
                    cardType={CardVariant.WITH_LIGHT_BG}
                    middleComponent={
                      <Item>
                        <Stack alignItems="center" spacing={3}>
                          <EmptyTeamsAndUsersIcon />
                          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                            Its lonely here
                          </WsTypography>
                          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                            No user tagged to this business unit.
                          </WsTypography>
                        </Stack>
                      </Item>
                    }
                  />
                </>
              ) : (
                <WsDataTable columns={columns} data={getSelectedManagers} selectable={false} />
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
export default BusinessUnitUsersTab;
